/* ============================================================================
 * appearance — the skin builder model
 * ----------------------------------------------------------------------------
 * THREE SKINS  choose a design language.            (skins/*.css)
 * KNOBS        adjust that language personally.     (this file + appearance.css)
 *
 * The knob vocabulary is NOT invented here. It is the seven controls already
 * defined by the Presence "nude" build (docs/NUDE-BUILD.md,
 * apps/web/app/nude/settings.tsx), which is the in-house precedent for
 * personal UI:
 *
 *   accent · ground · density · type · borders · hud · atmosphere
 *
 * This adds three that the desk shell needs and nude had no reason to expose:
 * radius, bevel, and the base-skin choice itself.
 *
 * Every value is a token remap, never a new hue. `canon` on any knob means
 * "whatever the active skin says" — so a personal setup layers on top of a
 * design language instead of replacing it, and switching base skins keeps the
 * rest of your setup intact.
 *
 * Precedent for portability: nude persists to localStorage and offers
 * Export/Import JSON + Reset to canon. Same contract here.
 * ========================================================================= */

import { DEFAULT_SKIN, SKINS, type Skin } from '../hooks/useSkin';

export interface Appearance {
  /** The design language being adjusted. */
  base: Skin;

  accent: 'canon' | 'brass' | 'hud' | 'monoBlue' | 'cream';
  ground: 'canon' | 'navy' | 'ink' | 'slate';
  density: 'canon' | 'comfortable' | 'compact';
  type: 'canon' | 'mono' | 'sans' | 'mixed';
  borders: 'canon' | 'hairline' | 'none';
  radius: 'canon' | '0' | '2' | '4';
  hud: 'canon' | 'off' | 'on';
  atmosphere: 'canon' | 'none' | 'grid' | 'scanlines';
  bevel: 'canon' | 'on' | 'off';
}

/** Every knob resolves to its skin's own value when set to `canon`. */
export const CANON: Appearance = {
  base: DEFAULT_SKIN,
  accent: 'canon',
  ground: 'canon',
  density: 'canon',
  type: 'canon',
  borders: 'canon',
  radius: 'canon',
  hud: 'canon',
  atmosphere: 'canon',
  bevel: 'canon',
};

export type KnobKey = Exclude<keyof Appearance, 'base'>;

export interface KnobDef {
  key: KnobKey;
  label: string;
  note: string;
  /** `canon` is always offered first and means "whatever the skin says". */
  options: { value: string; label: string; swatch?: string }[];
}

/** Accent swatches mirror nude's accent control exactly. */
const ACCENT_SWATCHES = {
  brass: '#C9A33A',
  hud: '#E0BE3F',
  monoBlue: '#6FA3E5',
  cream: '#F4ECD8',
} as const;

const GROUND_SWATCHES = {
  navy: '#0a1330',
  ink: '#070b16',
  slate: '#0d1f3d',
} as const;

export const KNOBS: readonly KnobDef[] = [
  {
    key: 'accent',
    label: 'Accent',
    note: 'Primary affordance. Canon is the skin’s own accent.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'brass', label: 'Brass', swatch: ACCENT_SWATCHES.brass },
      { value: 'hud', label: 'HUD yellow', swatch: ACCENT_SWATCHES.hud },
      { value: 'monoBlue', label: 'Mono blue', swatch: ACCENT_SWATCHES.monoBlue },
      { value: 'cream', label: 'Cream', swatch: ACCENT_SWATCHES.cream },
    ],
  },
  {
    key: 'ground',
    label: 'Ground',
    note: 'Page canvas, and the text that sits on it.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'navy', label: 'Navy', swatch: GROUND_SWATCHES.navy },
      { value: 'ink', label: 'Ink', swatch: GROUND_SWATCHES.ink },
      { value: 'slate', label: 'Slate', swatch: GROUND_SWATCHES.slate },
    ],
  },
  {
    key: 'density',
    label: 'Density',
    note: 'Padding, block rhythm and base type size.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'comfortable', label: 'Comfortable' },
      { value: 'compact', label: 'Compact' },
    ],
  },
  {
    key: 'type',
    label: 'Type',
    note: 'Chrome face. Dossier canon is JetBrains Mono.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'mono', label: 'Mono' },
      { value: 'sans', label: 'Sans' },
      { value: 'mixed', label: 'Mixed' },
    ],
  },
  {
    key: 'borders',
    label: 'Borders',
    note: 'Hairline rules, or none at all.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'hairline', label: 'Hairline' },
      { value: 'none', label: 'None' },
    ],
  },
  {
    key: 'radius',
    label: 'Radius',
    note: 'The design language caps this at 4px. No pills.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: '0', label: '0 — square' },
      { value: '2', label: '2px' },
      { value: '4', label: '4px' },
    ],
  },
  {
    key: 'hud',
    label: 'HUD brackets',
    note: 'Corner brackets. Canon is off in a clean register.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'off', label: 'Off' },
      { value: 'on', label: 'On' },
    ],
  },
  {
    key: 'atmosphere',
    label: 'Atmosphere',
    note: 'Global background layers. Never applied to individual cards.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'none', label: 'None' },
      { value: 'grid', label: 'Grid' },
      { value: 'scanlines', label: 'Scanlines' },
    ],
  },
  {
    key: 'bevel',
    label: 'Bevel',
    note: 'Two-tone depth on controls. Off gives flat sheets.',
    options: [
      { value: 'canon', label: 'Canon' },
      { value: 'on', label: 'On' },
      { value: 'off', label: 'Off' },
    ],
  },
] as const;

export const STORAGE_KEY = 'supercompute-webos:appearance:v1';

function isSkin(v: unknown): v is Skin {
  return typeof v === 'string' && (SKINS as readonly string[]).includes(v);
}

/** Accepts arbitrary JSON and returns something safe to apply. */
export function coerceAppearance(raw: unknown): Appearance {
  if (!raw || typeof raw !== 'object') return { ...CANON };
  const o = raw as Record<string, unknown>;
  const out: Appearance = { ...CANON, base: isSkin(o.base) ? o.base : DEFAULT_SKIN };
  for (const knob of KNOBS) {
    const v = o[knob.key];
    if (typeof v === 'string' && knob.options.some((opt) => opt.value === v)) {
      // every knob's option list includes 'canon', so this is type-safe enough
      (out as unknown as Record<string, string>)[knob.key] = v;
    }
  }
  return out;
}

export function serialize(a: Appearance): string {
  return JSON.stringify(a, null, 2);
}

export function parseAppearance(json: string): Appearance {
  return coerceAppearance(JSON.parse(json) as unknown);
}

/** How many knobs are away from canon — the "personalised" indicator. */
export function countOverrides(a: Appearance): number {
  return KNOBS.filter((k) => a[k.key] !== 'canon').length;
}

/** The `data-opt-*` attributes the stylesheet keys off. `canon` is omitted. */
export function appearanceAttributes(a: Appearance): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const knob of KNOBS) {
    const v = a[knob.key];
    if (v !== 'canon') attrs[`data-opt-${knob.key.toLowerCase()}`] = v;
  }
  return attrs;
}

export const OPT_ATTR_KEYS = KNOBS.map((k) => `data-opt-${k.key.toLowerCase()}`);
